import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import _ from 'lodash'
import {
  MaterialSummaryDto,
  MoneyPerQuantityDto,
  QuantityDto,
  QuantityDtoType,
} from '../../services/APIs/InternalAPI/internal-api.contracts'

export interface MaterialSummaryModel {
  model: MaterialSummaryDto
  purchasingData: PurchasingData
}

export class PurchasingData {
  public id: string
  public grossPriceUpdatedOn: string
  public grossPrice: MoneyPerQuantityDto
  public preferredSupplier: Supplier

  /**
   * calculated field with the gross price per one unit
   */
  public grossPricePerMass: MoneyPerQuantityDto
  public grossPriceSource: GrossPriceSource
  public defaultPurchasingQuantity: QuantityDto
  public purchasingUnit: QuantityDto
}

export enum Supplier {
  NotInitialized = 0,
  MCB = 1,
}

export enum GrossPriceSource {
  NotInitialized = 0,
  StandardCatalog = 1,
  OrganizationLevelDefaults = 2,
  SupplyChainIntegration = 3,
}

export type SettingsState = {
  MaterialSettingsList: MaterialSummaryModel[]
}

const initialState: SettingsState = {
  MaterialSettingsList: undefined,
}

const settingsSlice = createSlice({
  initialState: initialState,
  name: 'settings',
  reducers: {
    clearMaterialSettingsList: (state) => {
      state = initialState
    },
    materialDataReceived: (
      state,
      action: PayloadAction<{ materialData: MaterialSummaryModel }>
    ) => {
      state.MaterialSettingsList = state.MaterialSettingsList.map((x) => {
        return x.model.id === action.payload.materialData.model.id
          ? action.payload.materialData
          : x
      })
    },
    materialSettingsListReceived: (
      state,
      action: PayloadAction<{ materialsSettings: MaterialSummaryModel[] }>
    ) => {
      state.MaterialSettingsList = action.payload.materialsSettings.sort(
        (a, b) => a.model.id.localeCompare(b.model.id, 'en', { numeric: true })
      )
    },
    materialSettingsItemReceived: (
      state,
      action: PayloadAction<{ materialSetting: MaterialSummaryModel }>
    ) => {
      const currentSettingIndex =
        state.MaterialSettingsList &&
        state.MaterialSettingsList.findIndex(
          (x) => x.model.id === action.payload.materialSetting.model.id
        )

      if (currentSettingIndex !== -1) {
        state.MaterialSettingsList[currentSettingIndex] =
          action.payload.materialSetting
      } else {
        state.MaterialSettingsList.push(action.payload.materialSetting)
        state.MaterialSettingsList.sort((a, b) => {
          return a.model.description.localeCompare(b.model.description, 'en', {
            numeric: true,
          })
        })
      }
    },
    settingsItemChanged: (
      state,
      action: PayloadAction<{
        materialId: string
        purchasingData: PurchasingData
      }>
    ) => {
      const materialSetting = state.MaterialSettingsList.find(
        (x) => x.model.id === action.payload.materialId
      )

      if (!materialSetting) throw new Error(`material not found`)

      const defaultPurchasingQuantity: QuantityDto = {
        value: 0,
        unit: 'kg',
        quantityType: QuantityDtoType.Mass,
      }

      const newRow = _.merge({}, materialSetting, {
        purchasingData: _.merge(
          {},
          {
            ...action.payload.purchasingData,
            defaultPurchasingQuantity: {
              ...defaultPurchasingQuantity,
              ...action.payload.purchasingData.defaultPurchasingQuantity,
            },
          }
        ),
      })

      state.MaterialSettingsList = state.MaterialSettingsList.map((x) => {
        return x.model.id === action.payload.materialId ? newRow : x
      })
    },
  },
})

export default settingsSlice.reducer

export const settingsActions = settingsSlice.actions
